import React, { useEffect, useState } from "react";
import axios from "axios";
import { Settings, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../../firebase.init";


const API_BASE = "https://demo-email-marketing-1-f6e91377e127.herokuapp.com";


const getCategoryLabel = (cat) => {
  if (cat === 'forSubscriber') return 'For Subscriber';
  if (cat === 'forRegister') return 'For Register';
  return cat || 'Uncategorized';
};

const getCategoryColor = (cat) => {
  if (cat === 'forSubscriber') return 'bg-info';
  if (cat === 'forRegister') return 'bg-success';
  return 'bg-secondary';
};

const UserEmailTemplate = () => {
  const [user] = useAuthState(auth);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(""); // New category filter
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState(["forSubscriber", "forRegister"]);
  const [currentPage, setCurrentPage] = useState(1);
  const templatesPerPage = 6;

  const fetchTemplates = async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(`${API_BASE}/user-emails?ownerEmail=${user.email}`);
      setTemplates(res.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const fetchCategories = async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(`${API_BASE}/user-email-templates?ownerEmail=${user.email}`);
      const existingCategories = [...new Set(res.data.map(u => u.category).filter(Boolean))];
      setCategories(prev => [...new Set([...prev, ...existingCategories])]);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  useEffect(() => {
    if (user?.email) {
      fetchTemplates();
      fetchCategories();
    }
  }, [user]);

  const handleAddOrUpdate = async (template) => {
    const isContentValid = template.mode === "ai" 
      ? template.aiPrompt?.trim() 
      : template.body?.trim();

    if (
      !template.templateName?.trim() ||
      !template.subject?.trim() ||
      !isContentValid ||
      typeof template.day !== "number" ||
      !template.category?.trim()
    ) {
      alert("Please fill all required fields including category.");
      return;
    }

    const payload = { ...template, ownerEmail: user?.email };

    try {
      if (editingTemplate) {
        await axios.put(
          `${API_BASE}/edit-user-email/${editingTemplate._id}`,
          payload
        );
      } else {
        await axios.post(`${API_BASE}/add-user-email`, payload);
      }
      setEditingTemplate(null);
      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      console.error("Failed to save template:", error);
      alert("Error saving template");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await axios.delete(`${API_BASE}/delete-user-email/${id}?ownerEmail=${user?.email}`);
        fetchTemplates();
      } catch (error) {
        console.error("Failed to delete template:", error);
        alert("Error deleting template");
      }
    }
  };

  const handleToggleStatus = async (template) => {
    try {
      await axios.put(`${API_BASE}/edit-user-email/${template._id}`, {
        ...template,
        enabled: !template.enabled,
        ownerEmail: user?.email
      });
      fetchTemplates();
    } catch (err) {
      alert("Failed to update template status");
    }
  };

  const handleSendNow = async (template) => {
    if (!window.confirm(`Are you sure you want to send "${template.templateName}" to all eligible users now?`)) {
      return;
    }

    try {
      const res = await axios.post(`${API_BASE}/api/send-template-now/${template._id}`);
      alert(res.data.message || `Successfully sent to ${res.data.sentCount} users.`);
      fetchTemplates(); // Refresh to show updated logs if any
    } catch (error) {
      console.error("Failed to send template:", error);
      alert(error.response?.data?.message || "Error sending template");
    }
  };

  // Updated filtering to include category
  const filteredTemplates = templates.filter((t) => {
    const matchesSearch = t.templateName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "" || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const indexOfLast = currentPage * templatesPerPage;
  const indexOfFirst = indexOfLast - templatesPerPage;
  const currentTemplates = filteredTemplates.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filteredTemplates.length / templatesPerPage);

  const closeModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setViewTemplate(null);
  };

  return (
    <div className="container py-5">
      <Link 
        to="/" 
        className="btn btn-outline-primary rounded-pill px-3 py-1 d-inline-flex align-items-center gap-2 mb-4"
        style={{ fontSize: '0.85rem' }}
      >
        <ArrowLeft size={16} />
        Back to Dashboard
      </Link>
      <h2 className="text-center mb-4 fw-bold text-primary">
        Email Template Manager
      </h2>

      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="d-flex gap-2 flex-wrap">
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          <CategoryFilter
                  categoryFilter={categoryFilter}
                  setCategoryFilter={setCategoryFilter}
                  categories={categories}
                />
        </div>
       
        <div className="d-flex gap-2">
          <Link to="/admin/generate-email" className="btn btn-outline-primary">
            Generate Email Template
          </Link>
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowModal(true);
              setEditingTemplate(null);
              setViewTemplate(null);
            }}
          >
            Add Template
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped table-hover align-middle">
          <thead className="table-primary">
            <tr>
              <th>Template Name</th>
              <th>Subject</th>
              <th>Category</th>
              <th>Day</th>
              <th>Status</th>
              <th style={{ width: "250px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTemplates.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-muted">
                  No templates found.
                </td>
              </tr>
            ) : (
              currentTemplates.map((template) => (
                <tr key={template._id}>
                  <td className="text-white">{template.templateName}</td>
                  <td className="text-white">{template.subject}</td>
                  <td>
                    <span className={`badge ${getCategoryColor(template.category)}`}>
                      {getCategoryLabel(template.category)}
                    </span>
                  </td>
                  <td className="text-white">{template.day}</td>
                  <td>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={template.enabled}
                        onChange={() => handleToggleStatus(template)}
                      />
                    </div>
                  </td>
                  <td className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => {
                        setViewTemplate(template);
                        setShowModal(false);
                        setEditingTemplate(null);
                      }}
                    >
                      View
                    </button>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => {
                        setEditingTemplate(template);
                        setShowModal(true);
                        setViewTemplate(null);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(template._id)}
                    >
                      Delete
                    </button>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleSendNow(template)}
                    >
                      Send Now
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <nav className="d-flex justify-content-center mt-4">
          <ul className="pagination">
            {[...Array(totalPages)].map((_, i) => (
              <li
                key={i}
                className={`page-item ${i + 1 === currentPage ? "active" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {(showModal || viewTemplate) && (
        <div
          className="modal d-block"
          tabIndex="-1"
          onClick={closeModal}
          aria-modal="true"
          role="dialog"
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {viewTemplate
                    ? "View Template"
                    : editingTemplate
                    ? "Edit Template"
                    : "Add Template"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                {viewTemplate ? (
                  <>
                    <h6>
                      <strong>Template Name:</strong>{" "}
                      {viewTemplate.templateName || "-"}
                    </h6>
                    <h6>
                      <strong>Subject:</strong> {viewTemplate.subject || "-"}
                    </h6>
                    <h6>
                      <strong>Category:</strong> 
                      <span className={`badge ms-2 ${getCategoryColor(viewTemplate.category)}`}>
                        {getCategoryLabel(viewTemplate.category)}
                      </span>
                    </h6>
                    <h6>
                      <strong>Day:</strong> {viewTemplate.day || "-"}
                    </h6>
                    <h6>
                      <strong>Status:</strong>{" "}
                      {viewTemplate.enabled ? "Enabled" : "Disabled"}
                    </h6>
                    <hr />
                    <div
                      dangerouslySetInnerHTML={{ __html: viewTemplate.body }}
                      style={{ minHeight: "200px" }}
                    />
                  </>
                ) : (
                  <EmailForm
              onSubmit={handleAddOrUpdate}
              editingTemplate={editingTemplate}
              categories={categories}
            />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EmailForm = ({ onSubmit, editingTemplate, categories }) => {
  const [templateName, setTemplateName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [day, setDay] = useState("");
  const [category, setCategory] = useState("forSubscriber");
  const [enabled, setEnabled] = useState(true);
  const [mode, setMode] = useState("html");
  const [aiPrompt, setAiPrompt] = useState("");
  const [availableShortcodes, setAvailableShortcodes] = useState([
    "user_name",
    "user_email",
    "first_name",
  ]);

  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const res = await axios.get("https://demo-email-marketing-1-f6e91377e127.herokuapp.com/user-email-templates");
        const allFields = res.data.flatMap((u) => u.customFields || []);
        const uniqueShortcodes = [
          ...new Set(allFields.map((f) => f.shortcode).filter(Boolean)),
        ];
        setAvailableShortcodes((prev) => [
          ...new Set([...prev, ...uniqueShortcodes]),
        ]);
      } catch (err) {
        console.error("Failed to fetch custom fields:", err);
      }
    };
    fetchCustomFields();
  }, []);

  useEffect(() => {
    if (editingTemplate) {
      setTemplateName(editingTemplate.templateName || "");
      setSubject(editingTemplate.subject || "");
      setBody(editingTemplate.body || "");
      setDay(editingTemplate.day?.toString() || "");
      setCategory(editingTemplate.category || "forSubscriber");
      setEnabled(editingTemplate.enabled ?? true);
      setMode(editingTemplate.mode || "html");
      setAiPrompt(editingTemplate.aiPrompt || "");
    } else {
      setTemplateName("");
      setSubject("");
      setBody("");
      setDay("");
      setCategory("forSubscriber");
      setEnabled(true);
      setMode("html");
      setAiPrompt("");
    }
  }, [editingTemplate]);

  const insertShortcode = (shortcode) => {
    const code = `{{${shortcode}}}`;
    if (mode === "html") {
      setBody((prev) => prev + code);
    } else {
      setAiPrompt((prev) => prev + code);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const isContentValid = mode === "ai" ? aiPrompt.trim() : body.trim();

    if (!templateName.trim() || !subject.trim() || !isContentValid || !day || !category) {
      alert("All fields are required including category");
      return;
    }

    onSubmit({
      templateName,
      subject,
      body,
      day: Number(day),
      category,
      enabled,
      mode,
      aiPrompt,
    });

    setTemplateName("");
    setSubject("");
    setBody("");
    setDay("");
    setCategory("forSubscriber");
    setEnabled(true);
    setMode("html");
    setAiPrompt("");
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="form-label">Template Name *</label>
        <input
          type="text"
          className="form-control"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          required
        />
      </div>
      <div className="mb-3">
        <label className="form-label">Subject *</label>
        <input
          type="text"
          className="form-control"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
        />
      </div>
      <div className="mb-3">
        <label className="form-label">Category *</label>
        <select
          className="form-select"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          required
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {getCategoryLabel(cat)}
            </option>
          ))}
        </select>
      </div>
      <div className="mb-3">
        <label className="form-label">Day *</label>
        <input
          type="number"
          className="form-control"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          required
          min="1"
        />
      </div>
      <div className="mb-3">
        <label className="form-label d-block fw-bold">Editor Mode</label>
        <div className="btn-group w-100" role="group">
          <input
            type="radio"
            className="btn-check"
            name="mode"
            id="modeHtml"
            autoComplete="off"
            checked={mode === "html"}
            onChange={() => setMode("html")}
          />
          <label className="btn btn-outline-primary" htmlFor="modeHtml">Manual HTML</label>

          <input
            type="radio"
            className="btn-check"
            name="mode"
            id="modeAi"
            autoComplete="off"
            checked={mode === "ai"}
            onChange={() => setMode("ai")}
          />
          <label className="btn btn-outline-primary" htmlFor="modeAi">AI Prompt</label>
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label d-flex justify-content-between align-items-center">
          <span>Available Shortcodes</span>
          <span className="small text-muted">Click to insert</span>
        </label>
        <div className="d-flex flex-wrap gap-2 p-2 border rounded bg-light">
          {availableShortcodes.map((code) => (
            <button
              key={code}
              type="button"
              className="btn btn-sm btn-info text-white"
              onClick={() => insertShortcode(code)}
            >
              {code}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">{mode === "html" ? "Body (HTML allowed) *" : "AI Generation Prompt *"}</label>
        {mode === "html" ? (
          <textarea
            className="form-control"
            rows={8}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            placeholder="<h2>Hello {{user_name}}</h2>..."
          />
        ) : (
          <textarea
            className="form-control"
            rows={8}
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            required
            placeholder="Generate a welcome email for {{user_name}} from Acme Corp..."
          />
        )}
      </div>
      <div className="form-check form-switch mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <label className="form-check-label">Enable Template</label>
      </div>
      <button type="submit" className="btn btn-primary">
        {editingTemplate ? "Update Template" : "Add Template"}
      </button>
    </form>
  );
};

const SearchBar = ({ searchQuery, setSearchQuery }) => (
  <input
    type="text"
    className="form-control"
    placeholder="Search templates by name..."
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    style={{ maxWidth: "250px" }}
  />
);

const CategoryFilter = ({ categoryFilter, setCategoryFilter, categories }) => (
  <select
    className="form-select"
    value={categoryFilter}
    onChange={(e) => setCategoryFilter(e.target.value)}
    style={{ maxWidth: "200px" }}
  >
    <option value="">All Categories</option>
    {categories.map(cat => (
      <option key={cat} value={cat}>
      {cat === "forSubscriber" ? "For Subscriber" : cat === "forRegister" ? "For Register" : cat}
      </option>
    ))}
  </select>
);

export default UserEmailTemplate;